import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Eye,
  UserMinus,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { formatPayMonthLabel, formatInr, stepEmptyMessage } from "./runPayrollUtils";
import PayrollPendingLeaveModal from "./PayrollPendingLeaveModal";
import PayrollDeclarationsTdsPanel from "./PayrollDeclarationsTdsPanel";
import SalaryRevisionArrearsBreakdownModal from "./SalaryRevisionArrearsBreakdownModal";
import LopReversalBreakdownModal from "./LopReversalBreakdownModal";
import LeftEmployeesFnfModal from "./LeftEmployeesFnfModal";
import EmpCodeNameSearchBar from "../../Common/EmpCodeNameSearchBar";
import { filterRowsByEmpCodeOrName } from "../../../lib/empCodeNameSearch";
import { exportLopAttendanceExcel } from "./exportLopAttendanceExcel";
import CutoffImpactPanel from "../shared/CutoffImpactPanel";

function SummaryBadge({ label, count, onClick, darkMode, tone = "default" }) {
  const tones = {
    default: darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]",
    warn: "border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900",
    ok: "border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900",
    danger: "border-red-300 bg-red-50 hover:bg-red-100 text-red-900",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`rounded-lg border px-3 py-2 text-left transition ${tones[tone] || tones.default} ${
        onClick ? "cursor-pointer" : "cursor-default opacity-90"
      }`}
    >
      <p className="text-[10px] font-medium uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-lg font-bold tabular-nums">{count ?? 0}</p>
    </button>
  );
}

function formatCell(row, col) {
  const v = row[col.key];
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ") || "—";
  if (col.format === "inr") {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return formatInr(n);
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function compareSortValues(a, b, key) {
  const va = a?.[key];
  const vb = b?.[key];
  const na = Number(va);
  const nb = Number(vb);
  if (Number.isFinite(na) && Number.isFinite(nb) && String(va).trim() !== "" && String(vb).trim() !== "") {
    return na - nb;
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

/** Employee/result table: Sl. No + clickable sortable headers + emp search. */
function StepDataTable({
  columns,
  rows,
  darkMode,
  emptyMessage,
  maxHeightClass = "max-h-[min(48vh,100%)]",
  showSerial = true,
  freezeLeading = 0,
  enableSearch = true,
}) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");

  const sourceRows = Array.isArray(rows) ? rows : [];
  const filteredRows = useMemo(
    () => (enableSearch ? filterRowsByEmpCodeOrName(sourceRows, search) : sourceRows),
    [enableSearch, sourceRows, search]
  );

  const sortedRows = useMemo(() => {
    const list = [...filteredRows];
    if (!sortKey) return list;
    list.sort((a, b) => {
      const cmp = compareSortValues(a, b, sortKey);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [filteredRows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const stickyBg = darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-surface))]";
  const stickyHeadBg = darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]";
  const freezeCount = Math.max(0, Number(freezeLeading) || 0);
  // Approx sticky left offsets: Sl.No + Code + Name
  const stickyLeftFor = (colIndexWithSerial) => {
    if (colIndexWithSerial === 0) return 0;
    if (colIndexWithSerial === 1) return 52;
    if (colIndexWithSerial === 2) return 52 + 88;
    return 52 + 88 + 140;
  };

  const renderYesNo = (value) => {
    const v = String(value ?? "").trim();
    if (/^no$/i.test(v)) {
      return <span className="font-semibold text-red-600">No</span>;
    }
    if (/^yes$/i.test(v)) {
      return <span className={darkMode ? "text-emerald-400" : "text-emerald-700"}>Yes</span>;
    }
    return v || "—";
  };

  if (!sourceRows.length) {
    return (
      <p
        className={`rounded-lg border border-dashed px-4 py-6 text-center text-sm ${
          darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
        }`}
      >
        {emptyMessage || "No records."}
      </p>
    );
  }

  return (
    <div className="flex min-h-0 flex-col gap-2">
      {enableSearch && (
        <EmpCodeNameSearchBar
          value={search}
          onChange={setSearch}
          darkMode={darkMode}
          showCount
          total={sourceRows.length}
          filtered={sortedRows.length}
        />
      )}
      {!sortedRows.length ? (
        <p
          className={`rounded-lg border border-dashed px-4 py-6 text-center text-sm ${
            darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
          }`}
        >
          No employees match “{search.trim()}”.
        </p>
      ) : (
    <div
      className={`${maxHeightClass} overflow-auto overscroll-contain rounded-lg border text-xs ${
        darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"
      }`}
    >
      <table className="w-full min-w-[980px] border-separate border-spacing-0">
        <thead className={`sticky top-0 z-20 ${stickyHeadBg}`}>
          <tr>
            {showSerial && (
              <th
                className={`px-2 py-2 text-left font-bold whitespace-nowrap ${
                  freezeCount > 0 ? `sticky z-30 ${stickyHeadBg}` : ""
                }`}
                style={freezeCount > 0 ? { left: stickyLeftFor(0) } : undefined}
              >
                Sl. No
              </th>
            )}
            {columns.map((c, ci) => {
              const freezeIdx = showSerial ? ci + 1 : ci;
              const freeze = freezeIdx < freezeCount;
              return (
                <th
                  key={c.key}
                  className={`px-2 py-2 font-bold whitespace-nowrap ${
                    c.format === "inr" || c.align === "right" ? "text-right" : "text-left"
                  } ${freeze ? `sticky z-30 ${stickyHeadBg}` : ""}`}
                  style={freeze ? { left: stickyLeftFor(freezeIdx) } : undefined}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(c.key)}
                    className={`font-bold hover:text-[hsl(var(--ds-primary))] ${
                      c.format === "inr" || c.align === "right" ? "w-full text-right" : "text-left"
                    } ${sortKey === c.key ? "text-[hsl(var(--ds-primary))]" : ""}`}
                    title={`Sort by ${c.label}`}
                  >
                    {c.label}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, i) => (
            <tr
              key={row.employeeId ? `${row.employeeId}-${row.statusCode || ""}-${i}` : row.empCode || i}
              className={darkMode ? "border-t border-[#3C3C3C]" : "border-t border-[hsl(var(--ds-border))]"}
            >
              {showSerial && (
                <td
                  className={`px-2 py-1.5 tabular-nums whitespace-nowrap text-[hsl(var(--ds-muted-fg))] ${
                    freezeCount > 0 ? `sticky z-10 ${stickyBg}` : ""
                  }`}
                  style={freezeCount > 0 ? { left: stickyLeftFor(0) } : undefined}
                >
                  {i + 1}
                </td>
              )}
              {columns.map((c, ci) => {
                const freezeIdx = showSerial ? ci + 1 : ci;
                const freeze = freezeIdx < freezeCount;
                const raw =
                  typeof c.render === "function" ? c.render(row) : formatCell(row, c);
                const content = c.yesNo ? renderYesNo(raw) : raw;
                return (
                  <td
                    key={c.key}
                    className={`px-2 py-1.5 tabular-nums whitespace-nowrap ${
                      c.format === "inr" || c.align === "right" ? "text-right" : "text-left"
                    } ${freeze ? `sticky z-10 ${stickyBg}` : ""}`}
                    style={freeze ? { left: stickyLeftFor(freezeIdx) } : undefined}
                  >
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
      )}
    </div>
  );
}

function DetailModal({ title, rows, columns, onClose, darkMode }) {
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100050] flex items-start justify-center bg-black/50 p-4 pt-20 pb-8"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`flex max-h-[calc(100vh-6rem)] w-full max-w-4xl flex-col overflow-hidden rounded-xl border shadow-xl ${
          darkMode ? "border-[#3C3C3C] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="payroll-detail-modal-title"
      >
        <div
          className={`flex shrink-0 items-center justify-between border-b px-4 py-3 ${
            darkMode ? "border-[#3C3C3C] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
          }`}
        >
          <h3 id="payroll-detail-modal-title" className="text-sm font-semibold">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`rounded p-1.5 ${
              darkMode ? "hover:bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]" : "hover:bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
            }`}
          >
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-3">
          <StepDataTable columns={columns} rows={rows} darkMode={darkMode} />
        </div>
      </div>
    </div>,
    document.body
  );
}

function AttendanceStepTabs({
  darkMode,
  monthLabel,
  locked,
  lopRows,
  pendingRows,
  pendingModal,
  setPendingModal,
  onPendingLeaveDone,
  onIgnoreLeaveIds,
  payMonth = "",
}) {
  const [tab, setTab] = useState("lops");
  const tabs = [
    { id: "lops", label: "LOPs", count: lopRows.length },
    { id: "pending", label: "Pending approval", count: pendingRows.length },
    { id: "cutoff", label: "Cut-off", count: null },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              tab === t.id
                ? "bg-[hsl(var(--ds-primary))] text-white"
                : darkMode
                  ? "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                  : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
            }`}
          >
            {t.label}
            {t.count != null ? ` (${t.count})` : ""}
          </button>
        ))}
        <span className={`ml-auto text-[11px] ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
          {monthLabel} · Month locked: {locked ? "Yes" : "No"}
        </span>
        {tab === "lops" ? (
          <button
            type="button"
            disabled={!lopRows.length}
            onClick={() => {
              exportLopAttendanceExcel({ rows: lopRows, monthLabel }).catch((err) => {
                window.alert(err?.message || "Could not export the LOP list");
              });
            }}
            className="rounded border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 py-1 text-[11px] font-semibold text-[hsl(var(--ds-fg))] disabled:opacity-45"
          >
            Export Excel
          </button>
        ) : null}
        {tab === "pending" && pendingRows.length > 0 ? (
          <button
            type="button"
            onClick={() => setPendingModal(true)}
            className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800"
          >
            Open leave actions
          </button>
        ) : null}
      </div>

      {tab === "cutoff" && (
        <CutoffImpactPanel payMonth={payMonth} darkMode={darkMode} className="min-h-0 flex-1" />
      )}

      {tab === "lops" && (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
          <p className={`shrink-0 text-[11px] ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
            H on a date is a half day. Leave balance is paid leave still left. Amber means leave remains and LOP was still marked.
          </p>
          <StepDataTable
            darkMode={darkMode}
            rows={lopRows}
            emptyMessage={`No LOP records for ${monthLabel}.`}
            columns={[
              { key: "empCode", label: "Code" },
              { key: "name", label: "Name" },
              {
                key: "leaveBalance",
                label: "Leave balance",
                align: "right",
                render: (row) => {
                  const n = Number(row.leaveBalance) || 0;
                  return (
                    <span className={n > 0 ? "font-semibold text-amber-700" : ""}>
                      {n}
                    </span>
                  );
                },
              },
              {
                key: "days",
                label: "LOP days",
                align: "right",
                render: (row) => (
                  <span className="font-semibold text-red-600">{row.days}</span>
                ),
              },
              { key: "lopDates", label: "LOP dates" },
              { key: "payDays", label: "Pay days", align: "right" },
            ]}
          />
        </div>
      )}

      {tab === "pending" && (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
          <StepDataTable
            darkMode={darkMode}
            rows={pendingRows}
            emptyMessage={`No pending leave approvals for ${monthLabel}.`}
            columns={[
              { key: "empCode", label: "Code" },
              { key: "name", label: "Name" },
              { key: "leaveType", label: "Leave type" },
              { key: "fromDate", label: "From" },
              { key: "toDate", label: "To" },
              { key: "status", label: "Status" },
            ]}
          />
        </div>
      )}

      {pendingModal && (
        <PayrollPendingLeaveModal
          darkMode={darkMode}
          rows={pendingRows}
          onClose={() => setPendingModal(false)}
          onDone={onPendingLeaveDone}
          onIgnoreIds={onIgnoreLeaveIds}
        />
      )}
    </div>
  );
}

function EmptyState({ message, darkMode }) {
  return (
    <p className={`rounded-lg border border-dashed px-4 py-6 text-center text-sm ${
      darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
    }`}>
      {message}
    </p>
  );
}

const PREVIEW_SKIP_KEYS = new Set(["preview_validate", "finalise", "payslips", "scope"]);

function formatWizardStepDetail(step, extras = null) {
  const key = step?.key;
  const s = step?.resultJson?.summary || {};
  if (key === "process") {
    const processed = Number(
      extras?.processedCount ?? s.processedCount ?? s.processed ?? 0
    );
    const total = Number(
      extras?.listedCount ?? s.totalCount ?? s.employeeCount ?? processed
    );
    const unprocessed = Math.max(0, total - processed);
    return `${processed}/${total} processed${unprocessed ? ` · ${unprocessed} unprocessed` : " · all done"}`;
  }
  if (key === "disbursement") {
    const paid = Number(extras?.disbPaid ?? s.paid ?? 0);
    const hold = Number(extras?.disbHold ?? s.hold ?? 0);
    const pending = Number(extras?.disbPending ?? s.pending ?? 0);
    const total = Number(extras?.disbTotal ?? s.total ?? paid + hold + pending);
    const complete = pending === 0 && total > 0;
    return `${paid} paid · ${hold} hold/partial · ${pending} unpaid · ${total} total${
      complete ? " · complete" : ""
    }`;
  }
  if (key === "checklist" || key === "company_checklist") {
    const done = Number(extras?.checklistCompleted ?? s.completed ?? 0);
    const total = Number(extras?.checklistTotal ?? s.totalItems ?? 0);
    return `${done}/${total} tasks done`;
  }
  if (typeof step?.resultJson?.summary === "object" && step.resultJson.summary) {
    const parts = Object.entries(step.resultJson.summary)
      .filter(([, v]) => v != null && typeof v !== "object")
      .slice(0, 4)
      .map(([k, v]) => `${k}: ${v}`);
    if (parts.length) return parts.join(" · ");
  }
  return step?.status || "—";
}

/**
 * Merge validation result rows with live wizard steps so Process / Disbursement /
 * Checklist always appear on Preview & Validate.
 */
function buildPreviewValidateRows({ resultRows = [], wizardSteps = null, extras = null }) {
  const byKey = new Map();
  (resultRows || []).forEach((r) => {
    if (r?.stepKey) byKey.set(r.stepKey, { ...r });
  });

  const order = [];
  const pushKey = (key) => {
    if (!key || PREVIEW_SKIP_KEYS.has(key) || order.includes(key)) return;
    order.push(key);
  };

  (wizardSteps || []).forEach((s) => pushKey(s.key));
  (resultRows || []).forEach((r) => pushKey(r.stepKey));
  ["process", "checklist", "disbursement"].forEach(pushKey);

  const stepByKey = new Map((wizardSteps || []).map((s) => [s.key, s]));

  return order.map((key) => {
    const step = stepByKey.get(key);
    const existing = byKey.get(key);
    const label =
      step?.label ||
      existing?.step ||
      (key === "process"
        ? "Process Payroll"
        : key === "disbursement"
          ? "Disbursement"
          : key === "checklist"
            ? "Checklist"
            : key);

    let status = existing?.status || step?.status || "pending";
    let detail = existing?.detail;

    if (key === "process" || key === "disbursement" || key === "checklist") {
      detail = formatWizardStepDetail(step || { key, resultJson: existing }, extras);
      if (key === "process" && extras?.processedCount != null && extras?.listedCount != null) {
        status =
          extras.listedCount > 0 && extras.processedCount >= extras.listedCount
            ? "passed"
            : extras.processedCount > 0
              ? "acknowledged"
              : status || "pending";
      }
      if (key === "disbursement" && extras?.disbTotal != null) {
        const allInScopeProcessed =
          extras.listedCount > 0 && extras.processedCount >= extras.listedCount;
        if (!allInScopeProcessed) {
          status = "acknowledged";
        } else {
          status =
            extras.disbTotal > 0 && Number(extras.disbPending || 0) === 0
              ? "passed"
              : Number(extras.disbPaid || 0) + Number(extras.disbHold || 0) > 0
                ? "acknowledged"
                : status || "pending";
        }
      }
      if (key === "checklist" && step?.resultJson?.summary) {
        status = step.status || existing?.status || "pending";
        detail = formatWizardStepDetail(step, extras);
      }
    }

    return {
      step: label,
      stepKey: key,
      status,
      detail: detail || "—",
    };
  });
}

export default function RunPayrollStepViews({
  stepKey,
  result,
  payMonth,
  darkMode = false,
  onNotify,
  onShowBreakdown,
  onShowArrears,
  onPendingLeaveDone,
  onIgnoreLeaveIds,
  onIgnoreStep,
  onGoToStep,
  showAllTds = false,
  onToggleShowAllTds,
  onRefreshStep,
  salaryStructures = null,
  statutoryConfigJson = null,
  branchesList = null,
  listedEmployees = null,
  processedIdSet = null,
  /** Full run stepsJson — used to ensure Preview cards include Process / Disbursement / Checklist */
  wizardSteps = null,
  previewExtras = null,
}) {
  const [detail, setDetail] = useState(null);
  const [pendingModal, setPendingModal] = useState(false);
  const [arrearsBreakdownRow, setArrearsBreakdownRow] = useState(null);
  const [lopReversalBreakdownRow, setLopReversalBreakdownRow] = useState(null);
  const [fnfEmployee, setFnfEmployee] = useState(null);

  const renderArrearsEye = (row) => {
    const text = row.breakdown || row.differentialBreakdown || "—";
    const canOpen = row.arrearsDetail || Math.abs(Number(row.arrearsAmount ?? row.differentialAmount) || 0) >= 0.01;
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="max-w-[220px] truncate" title={String(text)}>
          {String(text)}
        </span>
        {canOpen ? (
          <button
            type="button"
            title="View present / revised / differential by component"
            aria-label="View arrears breakdown"
            className={`inline-flex shrink-0 rounded p-1 ${
              darkMode ? "text-red-400 hover:bg-[hsl(var(--ds-muted))]" : "text-[hsl(var(--ds-primary))] hover:bg-red-50"
            }`}
            onClick={() => setArrearsBreakdownRow(row)}
          >
            <Eye size={14} />
          </button>
        ) : null}
      </span>
    );
  };

  const renderLopReversalEye = (row) => {
    const canOpen =
      row.lopReversalDetail || Math.abs(Number(row.lopReversalAmount) || 0) >= 0.01;
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="tabular-nums">{formatInr(Number(row.lopReversalAmount) || 0)}</span>
        {canOpen ? (
          <button
            type="button"
            title="View LOP reversal calculation"
            aria-label="View LOP reversal calculation"
            className={`inline-flex shrink-0 rounded p-1 ${
              darkMode ? "text-red-400 hover:bg-[hsl(var(--ds-muted))]" : "text-[hsl(var(--ds-primary))] hover:bg-red-50"
            }`}
            onClick={() => setLopReversalBreakdownRow(row)}
          >
            <Eye size={14} />
          </button>
        ) : null}
      </span>
    );
  };
  const monthLabel = formatPayMonthLabel(payMonth);
  const rows = result?.rows || [];
  const sections = result?.sections || [];
  const summary = result?.summary || {};
  const meta = result?.meta || {};

  const pendingRows = useMemo(() => {
    const sec = sections.find((s) => s.title === "Pending leave approvals");
    return sec?.rows || meta.pendingApprovals || [];
  }, [sections, meta.pendingApprovals]);

  // ── TDS Review / Audit (post-Process; may have no result yet) ──
  if (stepKey === "tax_declarations") {
    return (
      <PayrollDeclarationsTdsPanel
        result={result}
        payMonth={payMonth}
        darkMode={darkMode}
        showAllTds={showAllTds}
        processedIdSet={processedIdSet}
        onToggleShowAll={onToggleShowAllTds}
        onRefresh={() => onRefreshStep?.("tax_declarations")}
      />
    );
  }

  if (!result && stepKey !== "preview_validate") {
    return (
      <EmptyState
        message="Click Validate to load results for this step."
        darkMode={darkMode}
      />
    );
  }

  // ── New joinees ──
  if (stepKey === "new_joinees") {
    if (!rows.length) return <EmptyState message={stepEmptyMessage(stepKey, payMonth)} darkMode={darkMode} />;
    return (
      <StepDataTable
        darkMode={darkMode}
        rows={rows}
        freezeLeading={3}
        columns={[
          { key: "empCode", label: "Code" },
          { key: "name", label: "Name" },
          { key: "doj", label: "DOJ" },
          { key: "colCtc", label: "CTC", yesNo: true },
          { key: "colSalaryStructure", label: "Salary Structure", yesNo: true },
          { key: "colUan", label: "UAN", yesNo: true },
          { key: "colPfAccount", label: "PF Account", yesNo: true },
          { key: "colEsiNumber", label: "ESI Number", yesNo: true },
          { key: "colBankDetails", label: "Bank Details", yesNo: true },
          { key: "colStatutory", label: "Statutory set", yesNo: true },
          { key: "colPfApplicable", label: "PF Applicable", yesNo: true },
          { key: "colEsiApplicable", label: "ESI Applicable", yesNo: true },
          { key: "colPtApplicable", label: "PT Applicable", yesNo: true },
          { key: "colGratuityApplicable", label: "Gratuity Applicable", yesNo: true },
          { key: "colForm11", label: "Form-11 (EPF)", yesNo: true },
          { key: "colFormF", label: "Form-F (POGA)", yesNo: true },
          { key: "colAppointment", label: "Signed Appointment Letter", yesNo: true },
          { key: "colForm2", label: "Form-2 (ESI)", yesNo: true },
        ]}
      />
    );
  }

  // ── Left / revisions / lop / bank empty states ──
  if (["left_employees", "salary_revisions", "lop_reversal_arrears", "bank_details", "one_time_inputs"].includes(stepKey)) {
    if (!rows.length && !sections.some((s) => s.rows?.length)) {
      return <EmptyState message={stepEmptyMessage(stepKey, payMonth)} darkMode={darkMode} />;
    }
  }

  if (stepKey === "left_employees" && rows.length) {
    return (
      <>
        <StepDataTable
          darkMode={darkMode}
          rows={rows}
          columns={[
            { key: "empCode", label: "Code" },
            { key: "name", label: "Name" },
            { key: "lastWorkingDay", label: "LWD" },
            { key: "fnfProcessMethod", label: "Process method" },
            { key: "leaveEncashment", label: "Leave Encash", format: "inr" },
            { key: "gratuity", label: "Gratuity", format: "inr" },
            { key: "noticePayOrRecovery", label: "Notice", format: "inr" },
            { key: "fnfNet", label: "F&F Net", format: "inr" },
            {
              key: "fnfStatus",
              label: "F&F Status",
              render: (row) => {
                const paid = row.fnfPaid || String(row.fnfStatus) === "paid";
                return (
                  <span
                    className={
                      paid
                        ? "font-semibold text-emerald-700"
                        : "text-[hsl(var(--ds-fg))]"
                    }
                  >
                    {paid ? "Paid" : row.fnfStatus || "—"}
                  </span>
                );
              },
            },
            {
              key: "fnfAction",
              label: "F&F",
              render: (row) => (
                <button
                  type="button"
                  onClick={() => setFnfEmployee(row)}
                  className="rounded border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 py-1 text-[11px] font-semibold text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-muted))]"
                >
                  {row.fnfPaid || String(row.fnfStatus) === "paid" ? "View F&F" : "Edit F&F"}
                </button>
              ),
            },
          ]}
        />
        {fnfEmployee ? (
          <LeftEmployeesFnfModal
            darkMode={darkMode}
            payMonth={payMonth}
            employee={fnfEmployee}
            onClose={() => setFnfEmployee(null)}
            onSaved={() => onRefreshStep?.("left_employees")}
            startAtReview
          />
        ) : null}
      </>
    );
  }

  if (stepKey === "salary_revisions" && rows.length) {
    const revisionCols = [
      { key: "empCode", label: "Code" },
      { key: "name", label: "Name" },
      { key: "previousCtc", label: "Previous CTC", format: "inr" },
      { key: "newCtc", label: "Revised CTC", format: "inr" },
      { key: "incrementPct", label: "%" },
      { key: "actionDate", label: "Action Date" },
      { key: "effectiveDate", label: "Increment Effective" },
      { key: "differentialAmount", label: "Differential", format: "inr" },
      { key: "differentialBreakdown", label: "Breakdown", render: renderArrearsEye },
      { key: "reason", label: "Reason" },
    ];
    return (
      <div className="space-y-2">
        <StepDataTable darkMode={darkMode} rows={rows} columns={revisionCols} />
        {arrearsBreakdownRow && (
          <SalaryRevisionArrearsBreakdownModal
            darkMode={darkMode}
            row={arrearsBreakdownRow}
            onClose={() => setArrearsBreakdownRow(null)}
            structures={salaryStructures}
            statutoryConfigJson={statutoryConfigJson}
            branchesList={branchesList}
            employees={listedEmployees}
          />
        )}
      </div>
    );
  }

  if (stepKey === "bank_details" && rows.length) {
    const bankCols = [
      { key: "empCode", label: "Code" },
      { key: "name", label: "Name" },
      { key: "previousBank", label: "Previous bank" },
      { key: "previousAccount", label: "Prev A/c" },
      { key: "bankName", label: "New bank" },
      { key: "accountNumber", label: "New A/c" },
      { key: "ifscCode", label: "IFSC" },
      { key: "effectiveMonth", label: "Effective" },
    ];
    return <StepDataTable darkMode={darkMode} rows={rows} columns={bankCols} />;
  }

  if (stepKey === "lop_reversal_arrears") {
    const lopCount = rows.length;
    const arrearsSec =
      sections.find((s) => String(s.title || "").toLowerCase().includes("arrears")) || null;
    const arrearsRows = arrearsSec?.rows || [];
    if (!lopCount && !arrearsRows.length) {
      return <EmptyState message={stepEmptyMessage(stepKey, payMonth)} darkMode={darkMode} />;
    }
    const lopCols = [
      { key: "empCode", label: "Code" },
      { key: "name", label: "Name" },
      { key: "lopReversalDays", label: "LOP reversal days" },
      { key: "lopMonths", label: "LOP month", render: (row) => {
        const fromRow = String(row.lopMonths || "").trim();
        if (fromRow) return fromRow;
        const months = (row.lopReversalDetail?.months || []).map((m) => m.lopMonth).filter(Boolean);
        return months.length ? months.join(", ") : "—";
      } },
      { key: "payMonth", label: "Pay month" },
      { key: "lopReversalAmount", label: "Amount", render: renderLopReversalEye },
    ];
    const arrearsCols = [
      { key: "empCode", label: "Code" },
      { key: "name", label: "Name" },
      { key: "previousCtc", label: "Previous CTC", format: "inr" },
      { key: "newCtc", label: "Revised CTC", format: "inr" },
      { key: "actionDate", label: "Action Date" },
      { key: "effectiveDate", label: "Increment Effective" },
      { key: "arrearsAmount", label: "Arrears amount", format: "inr" },
      { key: "breakdown", label: "Breakdown", render: renderArrearsEye },
    ];
    return (
      <div className="space-y-4">
        {lopCount > 0 && (
          <div className="space-y-2">
            <h4 className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
              LOP reversals
            </h4>
            <StepDataTable darkMode={darkMode} rows={rows} columns={lopCols} />
          </div>
        )}

        {arrearsRows.length > 0 && (
          <div className="space-y-2">
            <h4 className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
              Salary revision arrears
            </h4>
            <StepDataTable darkMode={darkMode} rows={arrearsRows} columns={arrearsCols} />
          </div>
        )}

        {arrearsBreakdownRow && (
          <SalaryRevisionArrearsBreakdownModal
            darkMode={darkMode}
            row={arrearsBreakdownRow}
            onClose={() => setArrearsBreakdownRow(null)}
            structures={salaryStructures}
            statutoryConfigJson={statutoryConfigJson}
            branchesList={branchesList}
            employees={listedEmployees}
          />
        )}
        {lopReversalBreakdownRow && (
          <LopReversalBreakdownModal
            darkMode={darkMode}
            row={lopReversalBreakdownRow}
            onClose={() => setLopReversalBreakdownRow(null)}
          />
        )}
      </div>
    );
  }

  // ── Attendance ──
  if (stepKey === "attendance_validation") {
    const lopRows =
      meta.lopEmployees ||
      sections.find((s) => s.title === "LOPs")?.rows ||
      [];
    const pending = pendingRows.length
      ? pendingRows
      : sections.find((s) => s.title === "Pending leave approvals")?.rows || [];

    return (
      <AttendanceStepTabs
        darkMode={darkMode}
        monthLabel={monthLabel}
        locked={!!summary.attendanceLocked}
        lopRows={lopRows}
        pendingRows={pending}
        pendingModal={pendingModal}
        setPendingModal={setPendingModal}
        onPendingLeaveDone={onPendingLeaveDone}
        onIgnoreLeaveIds={onIgnoreLeaveIds}
        payMonth={payMonth}
      />
    );
  }

  // ── Headcount ──
  if (stepKey === "headcount_reconcile") {
    const opening = sections.find((s) => s.title === "Opening active employees")?.rows || [];
    const joinees = sections.find((s) => s.title === "New joinees")?.rows || [];
    const left = sections.find((s) => s.title === "Left employees")?.rows || [];
    const leftKey = (r) => String(r.employeeId || r.empCode || "");
    const leftIds = new Set(left.map(leftKey).filter(Boolean));
    const closingRows = [
      ...opening.filter((r) => !leftIds.has(leftKey(r))),
      ...joinees.filter((r) => !leftIds.has(leftKey(r))),
    ];
    const closingCount = Number(summary.closing ?? closingRows.length);
    const cols = [
      { key: "empCode", label: "Code" },
      { key: "name", label: "Name" },
      { key: "doj", label: "DOJ" },
      { key: "category", label: "Category" },
    ];
    const summaryRows = [
      {
        key: "opening",
        label: "Opening Head count",
        count: summary.opening ?? opening.length,
        rows: opening,
        Icon: Users,
        tone: "text-sky-600",
      },
      {
        key: "new",
        label: "New Joinee",
        count: summary.newJoinees ?? joinees.length,
        rows: joinees,
        Icon: UserPlus,
        tone: "text-emerald-600",
      },
      {
        key: "left",
        label: "Left",
        count: summary.leftCount ?? left.length,
        rows: left,
        Icon: UserMinus,
        tone: "text-amber-600",
      },
      {
        key: "closing",
        label: "Closing",
        count: closingCount,
        rows: closingRows,
        Icon: Users,
        tone: "text-[hsl(var(--ds-primary))]",
      },
    ];

    return (
      <div className="mx-auto flex w-full max-w-xl flex-col gap-3">
        <div
          className={`overflow-hidden rounded-xl border text-sm ${
            darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          <table className="w-full">
            <thead className={darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"}>
              <tr>
                <th className="px-3 py-2.5 text-left text-xs font-bold">Particulars</th>
                <th className="px-3 py-2.5 text-right text-xs font-bold">Count</th>
                <th className="px-3 py-2.5 text-center text-xs font-bold">View</th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.map((r) => {
                const Icon = r.Icon;
                return (
                  <tr
                    key={r.key}
                    className={darkMode ? "border-t border-[#3C3C3C]" : "border-t border-[hsl(var(--ds-border))]"}
                  >
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center gap-2 font-bold">
                        <Icon size={16} className={r.tone} />
                        {r.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right text-base font-bold tabular-nums">
                      {r.count}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <button
                        type="button"
                        disabled={!r.rows.length}
                        title={r.rows.length ? `View ${r.label}` : "No employees"}
                        onClick={() =>
                          setDetail({
                            title: `${r.label} — ${monthLabel}`,
                            rows: r.rows,
                            columns: cols,
                          })
                        }
                        className={`inline-flex rounded p-1.5 disabled:opacity-30 ${
                          darkMode
                            ? "text-red-400 hover:bg-[hsl(var(--ds-muted))]"
                            : "text-[hsl(var(--ds-primary))] hover:bg-red-50"
                        }`}
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {detail && <DetailModal {...detail} onClose={() => setDetail(null)} darkMode={darkMode} />}
      </div>
    );
  }

  // ── Checklist ──
  if (stepKey === "company_checklist" || stepKey === "checklist") {
    const done = rows.filter((r) => r.completed);
    const pending = rows.filter((r) => !r.completed);
    return (
      <div className="flex flex-wrap gap-2">
        <SummaryBadge
          label="Checklist tasks"
          count={`${done.length}/${rows.length}`}
          darkMode={darkMode}
          tone={pending.length ? "warn" : "ok"}
          onClick={() => setDetail({
            title: "Checklist",
            rows,
            columns: [
              { key: "checklistName", label: "Checklist" },
              { key: "label", label: "Task" },
              { key: "completed", label: "Done" },
            ],
          })}
        />
        {detail && <DetailModal {...detail} onClose={() => setDetail(null)} darkMode={darkMode} />}
      </div>
    );
  }

  // ── Preview cards (all steps before Finalise, including Process / Disbursement / Checklist) ──
  if (stepKey === "preview_validate") {
    const previewRows = buildPreviewValidateRows({
      resultRows: rows,
      wizardSteps,
      extras: previewExtras,
    });
    const blockingFailed = previewRows.filter((r) => {
      const st = r.status || "pending";
      if (st === "passed" || st === "acknowledged") return false;
      if (st !== "failed" && st !== "pending") return false;
      // Match backend: partial Process Payroll is not blocking
      if (r.stepKey === "process" && previewExtras?.processedCount > 0) return false;
      return st === "failed" || st === "pending";
    });
    const hardFailed = blockingFailed.filter((r) => r.status === "failed");
    return (
      <div className="space-y-3 overflow-auto">
        {hardFailed.length > 0 && (
          <p className="text-xs text-amber-800 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            {hardFailed.length} step(s) still blocking — open each step or use{" "}
            <strong>Ignore &amp; proceed</strong> below, then run check again.
          </p>
        )}
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {previewRows.map((row) => {
            const st = row.status || "pending";
            const tone =
              st === "passed" ? "ok" : st === "failed" ? "danger" : st === "acknowledged" ? "warn" : "default";
            const canIgnoreCard = st === "failed" && row.stepKey && onIgnoreStep;
            return (
              <div
                key={row.stepKey || row.step}
                className={`rounded border bg-transparent px-2 py-1.5 ${
                  tone === "ok"
                    ? "border-emerald-500"
                    : tone === "danger"
                      ? "border-red-500"
                      : tone === "warn"
                        ? "border-amber-500"
                        : darkMode
                          ? "border-[hsl(var(--ds-border))]"
                          : "border-[hsl(var(--ds-border))]"
                }`}
              >
                <p className="truncate text-[9px] font-bold uppercase opacity-60">{row.step}</p>
                <p className="text-[11px] font-bold capitalize leading-tight">
                  {st === "acknowledged" ? "ignored / ok" : st}
                </p>
                <p className="mt-0.5 line-clamp-2 text-[10px] leading-snug opacity-80">{row.detail || "—"}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {canIgnoreCard && (
                    <button
                      type="button"
                      onClick={() => onIgnoreStep(row.stepKey)}
                      className="rounded border border-amber-300 bg-[hsl(var(--ds-surface))] px-1.5 py-0.5 text-[9px] font-medium text-amber-900"
                    >
                      Ignore
                    </button>
                  )}
                  {row.stepKey && onGoToStep && (
                    <button
                      type="button"
                      onClick={() => onGoToStep(row.stepKey)}
                      className="rounded border px-1.5 py-0.5 text-[9px] underline"
                    >
                      Open
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Fallback table for one_time_inputs etc. ──
  if (!rows.length) {
    return <EmptyState message={stepEmptyMessage(stepKey, payMonth)} darkMode={darkMode} />;
  }

  return (
    <>
      <SummaryBadge
        label="Records"
        count={rows.length}
        darkMode={darkMode}
        onClick={() => setDetail({
          title: stepKey,
          rows,
          columns: Object.keys(rows[0] || {}).slice(0, 6).map((k) => ({ key: k, label: k })),
        })}
      />
      {detail && <DetailModal {...detail} onClose={() => setDetail(null)} darkMode={darkMode} />}
    </>
  );
}
